import { FEAT, FEAT_BOSSES, FEAT_GUARDIANS, FEATS, NIGHTMARES, SKILLS, UNIQUES, type UniqueId } from '@somnhold/content';
import { dist2, imin, withinRange } from '../math/fixed.ts';
import type { BattleState, Bot, Enemy, FeatTally, Hero, Zone } from '../types.ts';
import { pstats } from './combat.ts';
import { roarSpared } from './panoply-barbarian.ts';

/**
 * The feats of the unique pieces in a fight (docs/UNICAS.md §2.3): what each hero did with the power of the pieces it
 * wears, tallied in `stats.feats` for the campaign to add to its feats (campaign/feats.ts). Nothing here changes the
 * fight: the hooks only count, from what the fight already did. Spectres count; mercenaries don't (nor the cast).
 *
 * Kept apart from the pieces' own code (battle/uniques.ts) and the skills' (cast.ts): those call a function of this file
 * with what they did, in one line.
 */

/** A hero's tally for a piece, when the piece's power counts for it (worn, and not a mercenary's or the cast's). */
const tallyOf = (s: BattleState, h: Hero, id: UniqueId): FeatTally | null => {
  if (h.control === 'merc' || !h.uq?.includes(id) || h.pid.startsWith('npc:')) return null;
  const all = (s.stats.feats ??= {});
  const mine = (all[h.pid] ??= {});
  return (mine[id] ??= { u: 0, t: 0, p: 0 });
};

const heroOfPid = (s: BattleState, pid: string): Hero | undefined => s.heroes.find((h) => h.pid === pid);
const heroOfId = (s: BattleState, id: number): Hero | undefined => s.heroes.find((h) => h.id === id);

/** Its Costumbre: `n` more times (or damage, healing…). */
export const featUse = (s: BattleState, h: Hero, id: UniqueId, n: number): void => {
  if (n <= 0) return;
  const t = tallyOf(s, h, id);
  if (t) t.u += n;
};

/** A moment of its Prueba. */
const test = (s: BattleState, h: Hero, id: UniqueId): void => {
  const t = tallyOf(s, h, id);
  if (t) t.t++;
};

/** Its power used once more (the Legend asks it a few times each boss fight). */
const power = (s: BattleState, h: Hero, id: UniqueId): void => {
  const t = tallyOf(s, h, id);
  if (t) t.p++;
};

/** A panoply piece's test moment (docs/PANOPLIA.md §10), counted by battle/panoply.ts where it happens. */
export const featPieceTest = (s: BattleState, h: Hero, id: UniqueId): void => test(s, h, id);

/** A panoply piece's test that waits to see how it ends: `need` of `ids` fallen within `ticks` (−1: the hero didn't fall). */
export const featPieceWatch = (s: BattleState, h: Hero, id: UniqueId, ids: number[], ticks: number, need: number): void => watch(s, h, id, ids, ticks, need);

/** Whether another hero counts as an ally for the tests that count them: a player's hero, live or as a spectre. */
const playerAlly = (o: Hero): boolean => o.control !== 'merc' && !o.pid.startsWith('npc:');

/** A panoply piece's test that counts allies (§2.2): the heroes it reached, the wearer left out. */
export const featPieceAllies = (s: BattleState, h: Hero, id: UniqueId, heroes: readonly Hero[]): void => {
  let players = 0;
  let any = 0;
  for (const o of heroes) {
    if (o.id === h.id) continue;
    any++;
    if (playerAlly(o)) players++;
  }
  if (any > 0) allyMoment(s, h, id, players, any);
};

/**
 * A moment of a test that counts allies (§2.2): how many players' heroes it reached, and how many heroes at all (the
 * cast and mercenaries count only in a campaign of one player, which the campaign decides). Indexes past 7 are 7.
 */
const allyMoment = (s: BattleState, h: Hero, id: UniqueId, players: number, any: number): void => {
  const t = tallyOf(s, h, id);
  if (!t) return;
  const tp = (t.tp ??= [0, 0, 0, 0, 0, 0, 0, 0]);
  const ta = (t.ta ??= [0, 0, 0, 0, 0, 0, 0, 0]);
  tp[imin(7, players)] = (tp[imin(7, players)] ?? 0) + 1;
  ta[imin(7, any)] = (ta[imin(7, any)] ?? 0) + 1;
};

/** Raises a moment already counted for this zone to a bigger one (the sanctuary and the bloom count once per cast). */
const allyRaise = (t: FeatTally, players: number, any: number, was: [number, number]): void => {
  const tp = (t.tp ??= [0, 0, 0, 0, 0, 0, 0, 0]);
  const ta = (t.ta ??= [0, 0, 0, 0, 0, 0, 0, 0]);
  if (players > was[0]) {
    if (was[0] > 0) tp[was[0]] = (tp[was[0]] ?? 1) - 1;
    tp[players] = (tp[players] ?? 0) + 1;
  }
  if (any > was[1]) {
    if (was[1] > 0) ta[was[1]] = (ta[was[1]] ?? 1) - 1;
    ta[any] = (ta[any] ?? 0) + 1;
  }
};

/** Watches a moment until `until`: it counts if `need` of `ids` fell by then (a hero watched, `need` −1: if it didn't fall). */
const watch = (s: BattleState, h: Hero, id: UniqueId, ids: number[], ticks: number, need: number): void => {
  const t = tallyOf(s, h, id);
  if (t && ids.length) (t.w ??= []).push({ until: s.tick + ticks, ids, need });
};

const isBig = (e: Enemy): boolean => !!NIGHTMARES[e.kind].big || e.elite;

/* ---------- what the skills did (cast.ts, one call at the end of a cast) ---------- */

/**
 * A skill was cast: the pieces that change it count. `hits` are the nightmares it caught, `vars` what its program kept
 * (the whirl's pulled, the roots' spread, the oath's and dawn's raised), `e` its target. An echo's second cast counts
 * what it does, not as a use of the power.
 */
export const featCast = (s: BattleState, h: Hero, skill: string, hits: Enemy[], vars: Record<string, number>, e: Enemy | null, killed: boolean, fork: string | null, echo: boolean, at: { x: number; y: number } | null = null): void => {
  if (!h.uq?.length) return;
  for (const id of h.uq) {
    if (UNIQUES[id].skill !== skill) continue;
    if (!echo) power(s, h, id);
    if (id === 'gateBlade') {
      let n = 0;
      let big = 0;
      for (const o of hits) {
        if (o.stun <= 0) continue;
        n++;
        if (isBig(o)) big++;
      }
      featUse(s, h, id, big);
      if (n >= FEAT.need.bash) test(s, h, id);
    } else if (id === 'gasparHammer') {
      if (e && e.stun > 0) {
        featUse(s, h, id, 1);
        if (e.elite) watch(s, h, id, [e.id], FEAT.watch.smite, 1);
      }
    } else if (id === 'oldRootStaff') {
      const held = hits.length + (vars.spread ?? 0);
      featUse(s, h, id, held);
      if (held >= FEAT.need.roots) test(s, h, id);
    } else if (id === 'wandererCloak') {
      featUse(s, h, id, hits.length);
      if (hits.some((o) => !!NIGHTMARES[o.kind].big || (fork === 'steelNet' && o.elite))) test(s, h, id);
    } else if (id === 'posternMedal' || id === 'oathPlate') {
      // Who this cast covers, and until when: the saves and the soaking go to whoever cast it (`r`: id, until, …).
      const t = tallyOf(s, h, id);
      if (!t) continue;
      const cover = (o: Hero, until: number): void => {
        const r = (t.r ??= []);
        for (let i = 0; i < r.length; i += 2) if (r[i] === o.id) {
          r[i + 1] = until;
          return;
        }
        r.push(o.id, until);
      };
      if (id === 'oathPlate') {
        const until = s.tick + SKILLS.aegis.duration * 2;
        cover(h, until);
        let best: Hero | null = null;
        if (at) for (const o of s.heroes) if (withinRange(o.x, o.y, at.x, at.y, 600) && (!best || dist2(o.x, o.y, at.x, at.y) < dist2(best.x, best.y, at.x, at.y))) best = o;
        if (best) cover(best, until);
      } else for (const o of s.heroes) if ((o.undying ?? 0) > 0 && withinRange(o.x, o.y, h.x, h.y, SKILLS.oath.radius * 2)) cover(o, s.tick + (o.undying ?? 0));
      if (id === 'posternMedal' && (vars.raised ?? 0) > 0) allyMoment(s, h, id, vars.raisedPlayers ?? 0, vars.raised ?? 0);
    } else if (id === 'healerRosary') {
      const raised = vars.raised ?? 0;
      if (id === 'healerRosary') featUse(s, h, id, raised);
      if (raised > 0) allyMoment(s, h, id, vars.raisedPlayers ?? 0, raised);
    } else if (id === 'tailFang') {
      const t = tallyOf(s, h, id);
      // An echo's leap is no leap of the barbarian's own: it doesn't move the streak.
      if (!t || echo) continue;
      if (killed) {
        t.u++;
        t.k = (t.k ?? 0) + 1;
        if (t.k >= FEAT.need.chain) {
          t.t++;
          t.k = 0;
        }
      } else t.k = 0;
    }
  }
};

/** The fallen master's staff: the leaps of a fireball, and the whole ball watched (all of five or more must fall). */
export const featStaff = (s: BattleState, h: Hero, blast: Enemy[], leaps: Enemy[]): void => {
  featUse(s, h, 'fallenStaff', leaps.length);
  const ids = [...new Set([...blast.map((o) => o.id), ...leaps.map((o) => o.id)])];
  if (ids.length >= FEAT.need.staff) watch(s, h, 'fallenStaff', ids, FEAT.watch.staff, ids.length);
};

/* ---------- what the pieces did on their own (uniques.ts) ---------- */

/** The frost mantle's ring: the frozen, watched until they thaw (four of them must fall). */
export const featMantle = (s: BattleState, h: Hero, frozen: Enemy[]): void => {
  power(s, h, 'frostMantle');
  featUse(s, h, 'frostMantle', frozen.length);
  if (frozen.length >= FEAT.need.mantle) watch(s, h, 'frostMantle', frozen.map((o) => o.id), FEAT.watch.mantle, FEAT.need.mantle);
};

/** The boar hide's frenzy started by itself: its kills count until it ends. */
export const featBoar = (s: BattleState, h: Hero): void => {
  power(s, h, 'boarHide');
  const t = tallyOf(s, h, 'boarHide');
  if (!t) return;
  t.k = 1;
  t.m = [pstats(s, h.pid).kills];
};

/** The lamp's heal: how much, and an ally under a fifth of its life watched five seconds. */
export const featLamp = (s: BattleState, h: Hero, ally: Hero, healed: number, hpBefore: number): void => {
  power(s, h, 'chapelLamp');
  featUse(s, h, 'chapelLamp', healed);
  if (hpBefore * 10000 < ally.stats.maxHp * FEAT.watch.lampBelowBp && healed > 0 && ally.id !== h.id) {
    const t = tallyOf(s, h, 'chapelLamp');
    // One watch per ally at a time.
    if (t && !(t.w ?? []).some((w) => w.need < 0 && w.ids[0] === ally.id)) watch(s, h, 'chapelLamp', [ally.id], FEAT.watch.lamp, -1);
  }
};

/** The watchtower bow's arrow through the line. */
export const featPierce = (s: BattleState, h: Hero, n: number): void => {
  power(s, h, 'watchBow');
  featUse(s, h, 'watchBow', n);
  if (n >= FEAT.need.pierce) test(s, h, 'watchBow');
};

/** The wall axe's whirl: how many it pulled in. */
export const featPull = (s: BattleState, h: Hero, n: number): void => {
  featUse(s, h, 'wallAxe', n);
  if (n >= FEAT.need.whirl) test(s, h, 'wallAxe');
};

/** The anvil harness's automaton burst: how many it killed. */
export const featBurst = (s: BattleState, owner: number, killed: number): void => {
  const h = heroOfId(s, owner);
  if (h && killed >= FEAT.need.burst) test(s, h, 'anvilHarness');
};

/* ---------- blows and wards (combat.ts) ---------- */

/** A blow taken struck back: the last watch while challenging, the dwarf bark in bear form; who struck is remembered. */
export const featThorns = (s: BattleState, h: Hero, from: Enemy, watchBack: number, barkBack: number): void => {
  for (const [id, n] of [['lastWatch', watchBack], ['dwarfBark', barkBack]] as const) {
    if (n <= 0) continue;
    const t = tallyOf(s, h, id);
    if (!t) continue;
    t.u += n;
    const seen = (t.m ??= []);
    if (!seen.includes(from.id)) seen.push(from.id);
  }
};

/** The postern medal's oath kept an ally from falling (once per ally every ten seconds). */
export const featSaved = (s: BattleState, h: Hero): void => {
  const o = caster(s, h, 'posternMedal');
  if (!o || o.id === h.id) return;
  const t = tallyOf(s, o, 'posternMedal');
  if (!t) return;
  // Pairs of ally and tick, by even index.
  const last = (t.m ??= []);
  let i = -1;
  for (let k = 0; k < last.length; k += 2) if (last[k] === h.id) i = k;
  if (i >= 0 && s.tick - (last[i + 1] ?? 0) < FEAT.savedEvery) return;
  if (i >= 0) last[i + 1] = s.tick;
  else last.push(h.id, s.tick);
  t.u++;
};

/**
 * Who cast the oath or the aegis covering this hero now: the wearer whose cast lists it (the latest one), else, for a
 * cover the fight didn't see cast, the first wearer (an aegis raised before the tally knew it).
 */
const caster = (s: BattleState, h: Hero, id: 'posternMedal' | 'oathPlate'): Hero | null => {
  let best: Hero | null = null;
  let until = -1;
  let first: Hero | null = null;
  for (const o of s.heroes) {
    if (!o.uq?.includes(id)) continue;
    first ??= o;
    const r = s.stats.feats?.[o.pid]?.[id]?.r ?? [];
    for (let k = 0; k < r.length; k += 2) if (r[k] === h.id && (r[k + 1] ?? 0) >= s.tick && (r[k + 1] ?? 0) > until) {
      until = r[k + 1] ?? 0;
      best = o;
    }
  }
  return best ?? first;
};

/** An aegis soaked `absorbed`; it broke taunting `taunted` (the oath plate's). Credited to the paladin with the plate. */
export const featAegis = (s: BattleState, h: Hero, absorbed: number, taunted: number): void => {
  const by = caster(s, h, 'oathPlate');
  if (!by) return;
  featUse(s, by, 'oathPlate', absorbed);
  if (by === h && taunted >= FEAT.need.aegis) test(s, h, 'oathPlate');
};

/** A ward spared `n`: the dwarf mail's sanctuary, if the hero stands in one. */
export const featWard = (s: BattleState, h: Hero, n: number): void => {
  if (n <= 0) return;
  // A clan roar's ward (the foreman's pelt counts what it spared).
  if (h.roarBy !== undefined) roarSpared(s, h, n);
  for (const z of s.zones) {
    if (z.kind !== 'sanctuary' || !z.follow || !withinRange(z.x, z.y, h.x, h.y, z.radius)) continue;
    const o = heroOfId(s, z.follow);
    if (o?.uq?.includes('dwarfMail')) {
      featUse(s, o, 'dwarfMail', n);
      return;
    }
  }
};

/* ---------- ground (skills.ts) ---------- */

/** A pulse of a hero's ground: what it dealt or healed and how many it reached (`inked`: hallowed ground's inked). */
export const featZone = (s: BattleState, z: Zone, amount: number, reached: number): void => {
  const h = heroOfPid(s, z.pid);
  if (!h?.uq?.length) return;
  const id: UniqueId | null =
    z.kind === 'storm' ? 'stormEye' : z.kind === 'rain' ? 'mossWhistle' : z.kind === 'hallow' ? 'vigilCandle' : z.kind === 'bloom' ? 'maelisSeed' : z.kind === 'grenade' ? 'maelisKit' : null;
  if (!id || !h.uq.includes(id)) return;
  const t = tallyOf(s, h, id);
  if (!t) return;
  t.u += amount;
  if (id === 'maelisKit') {
    // The grenade's heal: the allies it reached (not the artificer).
    let players = 0;
    let any = 0;
    for (const o of s.heroes) {
      if (o.id === h.id || o.downTicks > 0 || !withinRange(z.x, z.y, o.x, o.y, z.radius)) continue;
      any++;
      if (playerAlly(o)) players++;
    }
    if (any > 0) allyMoment(s, h, id, players, any);
    return;
  }
  const need = id === 'stormEye' ? FEAT.need.storm : id === 'mossWhistle' ? FEAT.need.rain : id === 'vigilCandle' ? FEAT.need.ink : 0;
  // Once per cast: the storm, the rain and the hallowed ground's test.
  if (need > 0 && reached >= need && t.z !== z.id) {
    t.z = z.id;
    t.t++;
  }
};

/* ---------- every tick ---------- */

/** Allies standing in a hero's zone of this kind (players' heroes, and anyone), not counting the hero. */
const alliesIn = (s: BattleState, h: Hero, z: Zone): [number, number] => {
  let players = 0;
  let any = 0;
  for (const o of s.heroes) {
    if (o.id === h.id || o.downTicks > 0 || !withinRange(z.x, z.y, o.x, o.y, z.radius)) continue;
    any++;
    if (playerAlly(o)) players++;
  }
  return [imin(7, players), imin(7, any)];
};

/** Twice the signed area of a, b, c (fixed units; the coordinates are small enough for doubles to stay exact). */
const cross = (ax: number, ay: number, bx: number, by: number, cx: number, cy: number): number => (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);

const inTriangle = (px: number, py: number, a: { x: number; y: number }, b: { x: number; y: number }, c: { x: number; y: number }): boolean => {
  const d1 = cross(a.x, a.y, b.x, b.y, px, py);
  const d2 = cross(b.x, b.y, c.x, c.y, px, py);
  const d3 = cross(c.x, c.y, a.x, a.y, px, py);
  return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
};

/** The garrison key: three of the artificer's turrets joined, and six nightmares or more inside them. */
const triangle = (s: BattleState, h: Hero): void => {
  const t = tallyOf(s, h, 'garrisonKey');
  if (!t || (t.k !== undefined && s.tick - t.k < FEAT.triangleEvery)) return;
  const link = UNIQUES.garrisonKey.fx.link as number;
  const mine = s.turrets.filter((o) => o.owner === h.id && !o.portable);
  for (let i = 0; i < mine.length; i++)
    for (let j = i + 1; j < mine.length; j++)
      for (let k = j + 1; k < mine.length; k++) {
        const a = mine[i]!;
        const b = mine[j]!;
        const c = mine[k]!;
        if (!withinRange(a.x, a.y, b.x, b.y, link) || !withinRange(b.x, b.y, c.x, c.y, link) || !withinRange(a.x, a.y, c.x, c.y, link)) continue;
        let n = 0;
        for (const e of s.enemies) if (e.hp > 0 && inTriangle(e.x, e.y, a, b, c)) n++;
        if (n >= FEAT.need.triangle) {
          t.t++;
          t.k = s.tick;
          return;
        }
      }
};

/** A panoply piece's test that counts allies (§2.2): the heroes it reached but its wearer. */
export const featPieceAlly = (s: BattleState, h: Hero, id: UniqueId, reached: readonly Hero[]): void => {
  let players = 0;
  let any = 0;
  for (const o of reached) {
    if (o.id === h.id) continue;
    any++;
    if (playerAlly(o)) players++;
  }
  if (any > 0) allyMoment(s, h, id, players, any);
};

/** The watched moments: counted when enough fell (or, for an ally, when the time ran out with it standing). */
const resolveWatches = (s: BattleState, t: FeatTally): void => {
  if (!t.w?.length) return;
  const keep: NonNullable<FeatTally['w']> = [];
  for (const w of t.w) {
    // An ally watched (the lamp): `need` −1.
    if (w.need < 0) {
      const o = heroOfId(s, w.ids[0] as number);
      if (!o || o.downTicks > 0) continue;
      if (s.tick >= w.until) t.t++;
      else keep.push(w);
      continue;
    }
    let fell = 0;
    for (const id of w.ids) {
      const e = s.enemies.find((o) => o.id === id);
      if (!e || e.hp <= 0) fell++;
    }
    if (fell >= w.need) t.t++;
    else if (s.tick < w.until) keep.push(w);
  }
  t.w = keep;
};

/** The automaton of the anvil harness: the damage it took (its life lost since a tick ago). */
const automatonHurt = (s: BattleState, h: Hero, t: FeatTally): void => {
  const b: Bot | undefined = s.bots?.find((o) => o.owner === h.id && !!o.burst);
  if (!b) {
    t.m = [];
    return;
  }
  const [id, last] = t.m ?? [];
  if (id === b.id && last !== undefined && last > b.hp) t.u += last - (b.hp > 0 ? b.hp : 0);
  t.m = [b.id, b.hp > 0 ? b.hp : 0];
};

/** After a whole challenge (the last watch) or bear form (the dwarf bark) without falling, with enough striking it. */
const endured = (h: Hero, t: FeatTally, on: boolean, need: number): void => {
  if (on) {
    if (h.downTicks > 0) t.k = 2;
    else if (t.k === undefined || t.k === 0) t.k = 1;
    return;
  }
  // Felled meanwhile (heroDown takes the taunt the same tick): it never counts.
  if (t.k === 1 && h.downTicks === 0 && (t.m?.length ?? 0) >= need) t.t++;
  t.k = 0;
  t.m = [];
};

/** Every tick, after the ground: what only time tells (who stood, who fell, how long). */
export const featsTick = (s: BattleState): void => {
  if (!s.stats.feats) {
    // Nothing worn counts here yet: only the pieces that start counting on their own look further.
    let any = false;
    for (const h of s.heroes) if (h.uq?.length && h.control !== 'merc') any = true;
    if (!any) return;
  }
  for (const h of s.heroes) {
    if (!h.uq?.length || h.control === 'merc' || h.pid.startsWith('npc:')) continue;
    for (const id of h.uq) {
      if (id === 'garrisonKey') {
        if (s.tick % 12 === 0) triangle(s, h);
        continue;
      }
      const mine = s.stats.feats?.[h.pid]?.[id];
      if (id === 'lastWatch' && mine) endured(h, mine, h.taunt > 0, FEAT.need.challenge);
      else if (id === 'dwarfBark' && mine) endured(h, mine, (h.bear ?? 0) > 0, FEAT.need.bear);
      else if (id === 'anvilHarness') automatonHurt(s, h, tallyOf(s, h, id) as FeatTally);
      else if (id === 'boarHide') {
        const t = tallyOf(s, h, id) as FeatTally;
        const on = (h.frenzy ?? 0) > 0;
        // The kills of a frenzy that started by itself.
        if (t.k === 1) {
          const kills = pstats(s, h.pid).kills;
          t.u += kills - (t.m?.[0] ?? kills);
          t.m = [kills];
          if (!on) t.k = 0;
        }
        // Any frenzy: from under a tenth of its life to over six tenths.
        const r = (t.r ??= [0]);
        if (!on) r[0] = 0;
        else if (r[0] === 0 && h.hp * 10000 < h.stats.maxHp * FEAT.need.lowBp) r[0] = 1;
        else if (r[0] === 1 && h.hp * 10000 > h.stats.maxHp * FEAT.need.highBp) {
          r[0] = 2;
          t.t++;
        }
      } else if (id === 'dwarfMail' || id === 'maelisSeed') {
        const kind = id === 'dwarfMail' ? 'sanctuary' : 'bloom';
        const z = s.zones.find((o) => o.kind === kind && o.follow === h.id);
        const t = tallyOf(s, h, id) as FeatTally;
        if (!z) {
          t.m = [];
          continue;
        }
        if (t.m?.[0] !== z.id) {
          t.m = [z.id, 0, 0];
          t.r = new Array(16).fill(0);
        }
        const [players, any] = alliesIn(s, h, z);
        const r = t.r as number[];
        const hold = FEAT.allyHold[id] as number;
        let bp = 0;
        let ba = 0;
        for (let k = 1; k <= 7; k++) {
          r[k] = k <= players ? (r[k] ?? 0) + 1 : 0;
          r[8 + k] = k <= any ? (r[8 + k] ?? 0) + 1 : 0;
          if ((r[k] ?? 0) >= hold) bp = k;
          if ((r[8 + k] ?? 0) >= hold) ba = k;
        }
        const was: [number, number] = [t.m[1] ?? 0, t.m[2] ?? 0];
        if (bp > was[0] || ba > was[1]) {
          allyRaise(t, bp > was[0] ? bp : was[0], ba > was[1] ? ba : was[1], was);
          t.m = [z.id, bp > was[0] ? bp : was[0], ba > was[1] ? ba : was[1]];
        }
      }
      if (mine) resolveWatches(s, mine);
    }
  }
};

/** A nightmare fell: the big bosses are remembered for the Legend (the campaign decides which count where). */
export const featFelled = (s: BattleState, e: Enemy): void => {
  // A copy of one of the Unsleeping (the Prestacaras') is no boss felled.
  if (e.un?.copy) return;
  const kind = e.un ? `un:${e.kind}` : FEAT_BOSSES.includes(e.kind) || FEAT_GUARDIANS.includes(e.kind) ? e.kind : null;
  if (!kind) return;
  // In the Chasm, the floor it fell on (its guardians count from a third of the hero's level): `kind@floor`.
  const key = s.dungeon?.sima ? `${kind}@${s.dungeon.sima.floor}` : kind;
  const felled = (s.stats.felled ??= []);
  if (!felled.includes(key)) felled.push(key);
};

/** The test a piece asks, as the campaign counts it (the allies' tests read their moments; the rest, `t`). */
export const featTests = (id: UniqueId, t: FeatTally, need: number, solo: boolean): number => {
  if (!FEATS[id].allies) return t.t;
  const h = (solo ? t.ta : t.tp) ?? [];
  let n = 0;
  for (let k = need; k < h.length; k++) n += h[k] ?? 0;
  return n;
};
